import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdSearch, MdFileDownload, MdVisibility, MdEmail } from 'react-icons/md';
import '../master/MasterData.css';

interface PayrollPeriod {
  periodId: number;
  periodName: string;
  periodStart: string;
  periodEnd: string;
  paymentDate: string;
  employeeCount: number;
  totalGrossPay: number;
  totalDeductions: number;
  totalNetPay: number;
  status: 'DRAFT' | 'CONFIRMED' | 'PAID' | 'CLOSED';
  generatedBy: string;
  confirmedBy: string;
}

const PayrollRegister: React.FC = () => {
  const [periods, setPeriods] = useState<PayrollPeriod[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');

  useEffect(() => {
    // TODO: Fetch payroll periods from API
    setPeriods([
      {
        periodId: 1,
        periodName: 'February 2025 - Period 2',
        periodStart: '2025-02-16',
        periodEnd: '2025-02-28',
        paymentDate: '2025-03-05',
        employeeCount: 45,
        totalGrossPay: 1375000,
        totalDeductions: 225000,
        totalNetPay: 1150000,
        status: 'PAID',
        generatedBy: 'HR Manager',
        confirmedBy: 'Finance Director'
      },
      {
        periodId: 2,
        periodName: 'March 2025 - Period 1',
        periodStart: '2025-03-01',
        periodEnd: '2025-03-15',
        paymentDate: '2025-03-20',
        employeeCount: 45,
        totalGrossPay: 1425000,
        totalDeductions: 238000,
        totalNetPay: 1187000,
        status: 'CONFIRMED',
        generatedBy: 'HR Manager',
        confirmedBy: 'Finance Director'
      },
      {
        periodId: 3,
        periodName: 'March 2025 - Period 2',
        periodStart: '2025-03-16',
        periodEnd: '2025-03-31',
        paymentDate: '2025-04-05',
        employeeCount: 45,
        totalGrossPay: 1450000,
        totalDeductions: 242500,
        totalNetPay: 1207500,
        status: 'DRAFT',
        generatedBy: 'HR Manager',
        confirmedBy: ''
      }
    ]);
  }, []);

  const filteredPeriods = periods.filter(period => {
    const matchesSearch = period.periodName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'ALL' || period.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'PAID': return 'badge-success';
      case 'CONFIRMED': return 'badge-info';
      case 'CLOSED': return 'badge-secondary';
      default: return 'badge-warning';
    }
  };

  const handleExport = (periodId: number) => {
    alert(`Export payroll register for period ${periodId} to Excel`);
  };

  const handleViewPayslips = (periodId: number) => {
    alert(`View all payslips for period ${periodId}`);
  };

  const handleEmailPayslips = (periodId: number) => {
    if (window.confirm('Send payslips to all employees via email?')) {
      alert('Payslips sent successfully');
    }
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Payroll Register</h2>
            <p className="subtitle">Historical payroll records and payslip management</p>
          </div>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search payroll periods..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-tabs">
              <button
                className={`filter-tab ${filterStatus === 'ALL' ? 'active' : ''}`}
                onClick={() => setFilterStatus('ALL')}
              >
                All Periods
              </button>
              <button
                className={`filter-tab ${filterStatus === 'DRAFT' ? 'active' : ''}`}
                onClick={() => setFilterStatus('DRAFT')}
              >
                Draft
              </button>
              <button
                className={`filter-tab ${filterStatus === 'CONFIRMED' ? 'active' : ''}`}
                onClick={() => setFilterStatus('CONFIRMED')}
              >
                Confirmed
              </button>
              <button
                className={`filter-tab ${filterStatus === 'PAID' ? 'active' : ''}`}
                onClick={() => setFilterStatus('PAID')}
              >
                Paid
              </button>
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Period Name</th>
                  <th>Period Dates</th>
                  <th>Payment Date</th>
                  <th>Employees</th>
                  <th>Total Gross Pay</th>
                  <th>Total Deductions</th>
                  <th>Total Net Pay</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPeriods.map(period => (
                  <tr key={period.periodId}>
                    <td className="name">{period.periodName}</td>
                    <td>
                      {new Date(period.periodStart).toLocaleDateString('en-US')} - {new Date(period.periodEnd).toLocaleDateString('en-US')}
                    </td>
                    <td>{new Date(period.paymentDate).toLocaleDateString('en-US')}</td>
                    <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{period.employeeCount}</td>
                    <td className="amount">₱{period.totalGrossPay.toLocaleString()}</td>
                    <td className="amount" style={{ color: '#dc3545' }}>₱{period.totalDeductions.toLocaleString()}</td>
                    <td className="amount" style={{ color: '#28a745', fontWeight: 'bold' }}>
                      ₱{period.totalNetPay.toLocaleString()}
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(period.status)}`}>
                        {period.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleViewPayslips(period.periodId)}
                          title="View Payslips"
                        >
                          <MdVisibility />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleExport(period.periodId)}
                          title="Export to Excel"
                          style={{ color: '#28a745' }}
                        >
                          <MdFileDownload />
                        </button>
                        {(period.status === 'CONFIRMED' || period.status === 'PAID') && (
                          <button
                            className="btn-icon btn-email"
                            onClick={() => handleEmailPayslips(period.periodId)}
                            title="Email Payslips"
                          >
                            <MdEmail />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredPeriods.length === 0 && (
              <div className="no-data">
                <p>No payroll periods found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default PayrollRegister;
